import { PageHeader, type SelectOption } from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AnalyticsPixel } from "../components/AnalyticsPixel"
import { IndividualDashboard } from "../components/IndividualDashboard"
import { OrganizationDashboard } from "../components/OrganizationDashboard"
import { TeamDashboard } from "../components/TeamDashboard"
import { getUrlParams, updateUrlParams } from "../hooks/useUrlParams"
import { useI18nStore, useMetadataStore, useViewStore } from "../stores/StoreContext"
import type { ViewType } from "../stores/ViewStore"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

// Valid view types for URL params
const validViews: ViewType[] = ["organization", "group", "individual"]
const validTimeRanges: TimeRange[] = ["7days", "14days", "28days", "8weeks", "12weeks"]
const validTabs = ["insights", "commits", "prs", "issues", "releases", "activity"]

export const HomePage = observer(() => {
  const viewStore = useViewStore()
  const metadataStore = useMetadataStore()
  const i18nStore = useI18nStore()

  // Track URL params reactively
  const [urlParams, setUrlParams] = useState(() => getUrlParams())

  // Update URL params when they change (listen for popstate and custom events)
  useEffect(() => {
    const updateParams = () => {
      setUrlParams(getUrlParams())
    }

    window.addEventListener("popstate", updateParams)
    // Custom event for when updateUrlParams is called
    window.addEventListener("urlparamschange", updateParams)

    return () => {
      window.removeEventListener("popstate", updateParams)
      window.removeEventListener("urlparamschange", updateParams)
    }
  }, [])

  const selectedTeam = urlParams.team
  const selectedContributor = urlParams.contributor

  // Initialize from URL params on mount (URL params take precedence over localStorage)
  // Also update when URL params change (e.g., from internal navigation)
  useEffect(() => {
    const params = getUrlParams()

    // Set view from URL if valid, otherwise keep localStorage value
    if (params.view && validViews.includes(params.view as ViewType)) {
      viewStore.setView(params.view as ViewType)
    }

    // Set time range from URL if valid, otherwise keep localStorage value
    if (params.timeRange && validTimeRanges.includes(params.timeRange as TimeRange)) {
      viewStore.setTimeRange(params.timeRange as TimeRange)
    }

    // Set tab from URL if valid, otherwise keep localStorage value
    if (params.tab && validTabs.includes(params.tab)) {
      viewStore.setActiveTab(params.tab)
    }
  }, [viewStore, urlParams])

  // Get time range from store
  const selectedTimeRange = viewStore.timeRange
  const selectedTab = viewStore.activeTab

  // Check if teams are available
  const hasTeams = metadataStore.uniqueTeams.length > 0

  // View options for the dropdown
  const viewOptions: SelectOption[] = useMemo(
    () => [
      { id: "organization", label: i18nStore.t("viewOrganization") },
      ...(hasTeams ? [{ id: "group", label: i18nStore.t("viewGroup") }] : []),
      { id: "individual", label: i18nStore.t("viewIndividual") },
    ],
    [hasTeams, i18nStore.t],
  )

  // Time range options for the dropdown
  const timeRangeOptions: SelectOption[] = useMemo(
    () => [
      { id: "7days", label: i18nStore.t("timeLast7Days") },
      { id: "14days", label: i18nStore.t("timeLast14Days") },
      { id: "28days", label: i18nStore.t("timeLast28Days") },
      { id: "8weeks", label: i18nStore.t("timeLast8Weeks") },
      { id: "12weeks", label: i18nStore.t("timeLast12Weeks") },
    ],
    [i18nStore.t],
  )

  // Handle view change with URL update
  const handleViewChange = useCallback(
    (viewId: string) => {
      viewStore.setView(viewId as ViewType)
      updateUrlParams({ view: viewId === "organization" ? undefined : viewId })
    },
    [viewStore],
  )

  // Handle time range change with URL update
  const handleTimeRangeChange = useCallback(
    (timeRange: TimeRange) => {
      viewStore.setTimeRange(timeRange)
      updateUrlParams({ timeRange: timeRange === "7days" ? undefined : timeRange })
    },
    [viewStore],
  )

  // Handle tab change with URL update
  const handleTabChange = useCallback(
    (tab: string) => {
      viewStore.setActiveTab(tab)
      updateUrlParams({ tab: tab === "insights" ? undefined : tab })
    },
    [viewStore],
  )

  // Get dashboard title and subtitle based on view
  const getDashboardInfo = () => {
    if (viewStore.isOrganizationView) {
      return {
        title: i18nStore.t("dashboardOrganizationTitle"),
        subtitle: i18nStore.t("dashboardOrganizationSubtitle"),
      }
    }
    if (viewStore.isGroupView) {
      return {
        title: i18nStore.t("dashboardTeamTitle"),
        subtitle: i18nStore.t("dashboardTeamSubtitleGeneric"),
      }
    }
    if (viewStore.isIndividualView) {
      return {
        title: i18nStore.t("dashboardIndividualTitle"),
        subtitle: i18nStore.t("dashboardIndividualSubtitleGeneric"),
      }
    }
    return {
      title: i18nStore.t("dashboardOrganizationTitle"),
      subtitle: i18nStore.t("dashboardOrganizationSubtitle"),
    }
  }

  const { title, subtitle } = getDashboardInfo()

  // Render dashboard based on selected view
  const renderDashboard = () => {
    if (viewStore.isOrganizationView) {
      return (
        <OrganizationDashboard
          timeRange={selectedTimeRange}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
        />
      )
    }

    if (viewStore.isGroupView) {
      return (
        <TeamDashboard
          timeRange={selectedTimeRange}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          initialTeam={selectedTeam}
        />
      )
    }

    if (viewStore.isIndividualView) {
      return (
        <IndividualDashboard
          timeRange={selectedTimeRange}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          initialContributor={selectedContributor}
        />
      )
    }

    return (
      <OrganizationDashboard
        timeRange={selectedTimeRange}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
      />
    )
  }

  return (
    <div className="min-h-full bg-background">
      <AnalyticsPixel source="main" />
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={title}
          subtitle={subtitle}
          selectOptions={viewOptions}
          selectedOptionId={viewStore.currentView}
          onSelectChange={handleViewChange}
          selectPlaceholder={i18nStore.t("viewSelectPlaceholder")}
          rightContent={
            <select
              value={selectedTimeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value as TimeRange)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={i18nStore.t("selectTimeRange")}
            >
              {timeRangeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          }
        >
          {renderDashboard()}
        </PageHeader>
      </div>
    </div>
  )
})
